/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { CSSTransition } from 'react-transition-group';
import PropTypes from 'prop-types';
import { PAGE_WIDTH, FULLBLEED_HEIGHT } from '@web-stories-wp/units';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import {
  useDebouncedCallback,
  useFocusOut,
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { hasGradient, PatternPropType } from '@web-stories-wp/patterns';
import { useKeyDownEffect } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import useFocusTrapping from '../../utils/useFocusTrapping';
import { useTransform } from '../transform';
import useStory from '../../app/story/useStory';
import { useCanvas } from '../../app';
import useRichText from '../richText/useRichText';
import { replaceGradientStylesInEditorState } from '../richText/formatters/color';
import SavedColorEditor from './savedColorEditor';
import GradientCanvasControls from './gradientCanvasControls';
import SavedColorPicker from './savedColorPicker';

const Container = styled.div`
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bg.secondary};
  width: 256px;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
  padding: 16px;

  &.picker-appear {
    opacity: 0.01;
    margin-top: -10px;

    &.picker-appear-active {
      opacity: 1;
      margin-top: 0;
      transition: 300ms ease-out;
      transition-property: opacity, margin-top;
    }
  }
`;

function createEditableColor(color, idOverride = null) {
  if (!color) {
    return null;
  }

  const id = color.id || idOverride || uuidv4();
  return {
    color: { ...color, id },
    id,
    name: '',
    error: null,
    isLocal: false,
    shouldSync: false,
  };
}

function getPatternSyncKey(pattern) {
  if (!pattern) {
    return '';
  }

  const { id, ...patternWithoutEditableId } = pattern;
  return JSON.stringify(patternWithoutEditableId);
}

function ColorPicker({
  onChange: onChangeProp,
  isEyedropperActive = false,
  color = null,
  geometryBounds: suppliedGeometryBounds,
  legacyPaint = 'css',
  isPageBackground = false,
  children = null,
  allowsGradient = false,
  allowsOpacity = true,
  editColorOnOpen = false,
  onEditingColorChange = null,
  onClose = () => {},
  changedStyle = 'background',
  syncSavedColors = true,
  updateSelectedElements = true,
  debounceLeading = true,
  deferCommitUntilPointerUp = false,
  onPreviewChange = () => {},
  onDimensionChange = () => {},
  setOnPickerClose = () => {},
}) {
  const pickerId = useRef(uuidv4()).current;
  const [paintMeasurement, setPaintMeasurement] = useState(null);
  const [editingColor, setEditingColor] = useState(() =>
    editColorOnOpen ? createEditableColor(color) : null
  );
  const [shouldEditSelectedElements, setShouldEditSelectedElements] =
    useState(true);
  const isEditingColor = Boolean(editingColor);
  useEffect(() => {
    onEditingColorChange?.(isEditingColor);
    return () => onEditingColorChange?.(false);
  }, [isEditingColor, onEditingColorChange]);
  const { hasCurrentEditor, editorState, updateEditorState } = useRichText(
    ({ state, actions }) => ({
      hasCurrentEditor: Boolean(state?.hasCurrentEditor),
      editorState: state?.editorState,
      updateEditorState: actions?.updateEditorState,
    })
  );
  const onChange = shouldEditSelectedElements ? onChangeProp : () => {};
  const onPreviewColorChange = shouldEditSelectedElements
    ? onPreviewChange
    : () => {};
  const showCustomPicker = useCallback(
    (color, isSelectedColor) => {
      setPaintMeasurement(null);
      setEditingColor(color);
      setShouldEditSelectedElements(isSelectedColor);
      onDimensionChange();
    },
    [onDimensionChange]
  );
  const hideCustomPicker = useCallback(() => {
    setPaintMeasurement(null);
    setEditingColor(null);
    onDimensionChange();
    setOnPickerClose(() => {});
    setShouldEditSelectedElements(true);
  }, [onDimensionChange]);

  useEffect(() => {
    if (!shouldEditSelectedElements) {
      return;
    }

    setEditingColor((currentEditingColor) => {
      if (
        currentEditingColor?.color &&
        color &&
        getPatternSyncKey(currentEditingColor.color) ===
          getPatternSyncKey(color)
      ) {
        return currentEditingColor;
      }

      if (!currentEditingColor) {
        return editColorOnOpen ? createEditableColor(color) : null;
      }

      // Applied Custom sessions also receive history through the color prop.
      // Retain the session identity/metadata so loading does not reset Cancel.
      return color
        ? {
            ...currentEditingColor,
            color: { ...color, id: currentEditingColor.color.id },
          }
        : null;
    });
  }, [color, editColorOnOpen, shouldEditSelectedElements]);

  const {
    actions: { pushTransform },
  } = useTransform();

  const {
    savedColors,
    selectedElementIds: actualSelectedElementIds = [],
    selectedElements = [],
    updateSavedColorState,
    updateSavedColors,
    updateElementsById,
  } = useStory(
    ({
      actions: { updateSavedColorState, updateSavedColors, updateElementsById },
      state: { savedColors, selectedElementIds, selectedElements },
    }) => ({
      savedColors,
      selectedElementIds,
      selectedElements,
      updateSavedColorState,
      updateSavedColors,
      updateElementsById,
    })
  );
  const { editingElementId } = useCanvas(({ state: { editingElement } }) => ({
    editingElementId: editingElement,
  }));

  const geometryBounds =
    suppliedGeometryBounds ||
    (selectedElements[0]?.isBackground
      ? { width: PAGE_WIDTH, height: FULLBLEED_HEIGHT }
      : {
          width: selectedElements[0]?.width,
          height: selectedElements[0]?.height,
        });
  const shouldUpdateElements =
    updateSelectedElements &&
    shouldEditSelectedElements &&
    !(changedStyle === 'color' && hasCurrentEditor);
  const selectedElementIds = useMemo(
    () => (shouldUpdateElements ? actualSelectedElementIds : []),
    [actualSelectedElementIds, shouldUpdateElements]
  );

  const lastAppliedColor = useRef(color);
  useEffect(() => {
    const changed = lastAppliedColor.current !== color;
    lastAppliedColor.current = color;
    if (changed && shouldUpdateElements) {
      // The persisted recipe owns paint after commit/history. Remove the inline
      // transform left by the picker so it cannot override Undo or Redo.
      selectedElementIds.forEach((id) => pushTransform(id, null));
    }
  }, [color, pushTransform, selectedElementIds, shouldUpdateElements]);

  const onDebouncedChange = useDebouncedCallback(onChange, 100, {
    leading: debounceLeading,
  });

  const onDebounceUpdateSavedColors = useDebouncedCallback(
    updateSavedColors,
    100,
    {
      leading: debounceLeading,
    }
  );
  // Local whole-text paint already previews through the transform channel.
  // Commit once on release instead of rebuilding the story on every slider tick.
  const shouldDeferChange =
    deferCommitUntilPointerUp ||
    (editingColor?.isLocal &&
      changedStyle === 'color' &&
      !hasCurrentEditor &&
      selectedElementIds.length > 0);
  const deferredChangeRef = useRef(null);
  const isPointerInteractingRef = useRef(false);

  const commitDeferredChange = useCallback(() => {
    document.removeEventListener('pointerup', commitDeferredChange);
    document.removeEventListener('pointercancel', commitDeferredChange);
    document.removeEventListener('mouseup', commitDeferredChange);
    isPointerInteractingRef.current = false;

    if (!deferredChangeRef.current) {
      return;
    }

    const deferredChange = deferredChangeRef.current;
    deferredChangeRef.current = null;
    onDebouncedChange.cancel();
    onChange(deferredChange);
  }, [onChange, onDebouncedChange]);

  const flushPendingColorChange = useCallback(() => {
    if (deferredChangeRef.current) {
      commitDeferredChange();
      return;
    }

    onDebouncedChange.flush();
  }, [commitDeferredChange, onDebouncedChange]);
  const flushPendingColorChangeRef = useRef(flushPendingColorChange);
  flushPendingColorChangeRef.current = flushPendingColorChange;

  const handlePointerInteractionStart = useCallback(() => {
    if (!shouldDeferChange || isPointerInteractingRef.current) {
      return;
    }

    isPointerInteractingRef.current = true;
    document.addEventListener('pointerup', commitDeferredChange);
    document.addEventListener('pointercancel', commitDeferredChange);
    document.addEventListener('mouseup', commitDeferredChange);
  }, [commitDeferredChange, shouldDeferChange]);

  useEffect(
    () => () => {
      document.removeEventListener('pointerup', commitDeferredChange);
      document.removeEventListener('pointercancel', commitDeferredChange);
      document.removeEventListener('mouseup', commitDeferredChange);
    },
    [commitDeferredChange]
  );

  useEffect(
    () => () => {
      flushPendingColorChangeRef.current();
    },
    []
  );

  const commitColorChange = useCallback(
    (color, debounce = true) => {
      if (!debounce) {
        deferredChangeRef.current = null;
        onDebouncedChange.cancel();
        onChange(color);
        return;
      }

      if (shouldDeferChange && isPointerInteractingRef.current) {
        deferredChangeRef.current = color;
        onDebouncedChange.cancel();
        return;
      }

      onDebouncedChange(color);
    },
    [shouldDeferChange, onChange, onDebouncedChange]
  );

  const updateInlineGradientStyles = useCallback(
    ({ action = 'addOrEdit', color }) => {
      if (
        !hasCurrentEditor ||
        changedStyle !== 'color' ||
        !editorState ||
        !updateEditorState ||
        !color?.id
      ) {
        return;
      }

      const updatedState = replaceGradientStylesInEditorState(editorState, {
        colorId: color.id,
        editedColor: color,
        action,
      });

      if (updatedState !== editorState) {
        updateEditorState(updatedState);
      }
    },
    [hasCurrentEditor, changedStyle, editorState, updateEditorState]
  );

  const handleLocalColorChange = useCallback(
    (newSavedColor, debounce = true) => {
      const newColor = newSavedColor.color;
      onPreviewColorChange(newColor);
      commitColorChange(newColor, debounce);
      updateInlineGradientStyles({ color: newColor });

      for (const selectedElementId of selectedElementIds) {
        pushTransform(
          selectedElementId,
          debounce
            ? {
                ...(changedStyle === 'color' &&
                hasGradient(newColor) &&
                !hasCurrentEditor
                  ? { gradientPreview: { color: newColor } }
                  : { color: newColor }),
                style: changedStyle,
                staticTransformation: true,
              }
            : null
        );
      }
    },
    [
      changedStyle,
      commitColorChange,
      pushTransform,
      onPreviewColorChange,
      selectedElementIds,
      updateInlineGradientStyles,
      hasCurrentEditor,
    ]
  );

  const closeIfNotEyedropping = (evt) => {
    if (evt?.target?.closest?.(`[data-gradient-picker-id="${pickerId}"]`))
      return;
    // Draft restores selection focus after inline formatting. The corresponding
    // pointerdown outside still dismisses the picker for a user canvas click.
    if (
      evt?.type === 'focusout' &&
      hasCurrentEditor &&
      changedStyle === 'color' &&
      evt.relatedTarget?.closest?.('[contenteditable="true"]')
    )
      return;
    if (!isEyedropperActive) {
      flushPendingColorChange();
      onClose(evt);
    }
  };

  // Detect focus out of color picker (clicks or focuses outside)
  const containerRef = useRef();
  useFocusOut(containerRef, closeIfNotEyedropping, [isEyedropperActive]);

  // Re-establish focus when actively exiting by button or key press
  const previousFocus = useRef(document.activeElement);
  const handleCloseAndRefocus = useCallback(
    (evt) => {
      // Ignore reason: In Jest, focus is always on document.body if not on any specific
      // element, so it can never be falsy, as it can be in a real browser.

      // istanbul ignore else
      if (previousFocus.current) {
        previousFocus.current.focus();
      }
      flushPendingColorChange();
      onClose(evt);
    },
    [flushPendingColorChange, onClose]
  );

  useKeyDownEffect(containerRef, 'esc', handleCloseAndRefocus);
  useFocusTrapping({ ref: containerRef });

  const handleColorChange = useCallback(
    (newSavedColor, debounce = true) => {
      if (newSavedColor.isLocal && !newSavedColor.shouldSync) {
        handleLocalColorChange(newSavedColor, debounce);
      } else {
        if (!debounce) {
          // A cancel/save is authoritative. Do not let an older queued preview
          // run after returning to the list and reapply its saved-colour edit.
          onDebounceUpdateSavedColors.cancel();
        }
        const onUpdateSavedColorsFn = debounce
          ? onDebounceUpdateSavedColors
          : updateSavedColors;

        onPreviewColorChange(newSavedColor.color);
        commitColorChange(newSavedColor.color, debounce);
        updateInlineGradientStyles({ color: newSavedColor.color });
        if (syncSavedColors) {
          onUpdateSavedColorsFn({
            action: 'addOrEdit',
            changedStyle,
            editedColor: newSavedColor,
            selectedElementIds,
            editingElementId: hasCurrentEditor ? editingElementId : null,
          });
        }
      }
    },
    [
      commitColorChange,
      handleLocalColorChange,
      onDebounceUpdateSavedColors,
      onPreviewColorChange,
      updateInlineGradientStyles,
      syncSavedColors,
    ]
  );

  // Separate handler for applying saved colors (clicking on them) without triggering sync
  const handleApplyColor = useCallback(
    (savedColor) => {
      // Only apply the color to the element, don't trigger saved colors sync
      // Use onChangeProp directly to ensure the color is always applied
      onChangeProp(savedColor.color);

      // Permanently update the element's color properties
      if (selectedElementIds.length > 0) {
        const updateProperties = {};

        if (changedStyle === 'color') {
          updateProperties.color = savedColor.color;
        } else if (
          changedStyle === 'border' ||
          changedStyle === 'border-color'
        ) {
          // Get the current border properties first
          const element = selectedElements[0];
          const currentBorder = element?.border || {};
          // Create a new border object with updated color
          updateProperties.border = {
            ...currentBorder,
            color: savedColor.color,
          };
        } else if (changedStyle === 'textShadow') {
          updateProperties.textShadowColor = savedColor.color;
        } else if (changedStyle === 'background') {
          updateProperties.backgroundColor = savedColor.color;
        }

        updateElementsById({
          elementIds: selectedElementIds,
          properties: updateProperties,
        });
      }
    },
    [
      onChangeProp,
      selectedElementIds,
      selectedElements,
      updateElementsById,
      changedStyle,
    ]
  );

  useEffect(() => {
    return () => {
      setShouldEditSelectedElements(true);
    };
  }, []);

  const savedColorWithId = useMemo(
    () =>
      syncSavedColors
        ? savedColors.find(
            (savedColor) => savedColor.color.id === editingColor?.color.id
          )
        : null,
    [editingColor?.color.id, savedColors, syncSavedColors]
  );
  const canvasElement = isPageBackground
    ? {
        id: 'page-background-gradient',
        isBackground: true,
        width: geometryBounds.width,
        height: geometryBounds.height,
      }
    : selectedElements.length === 1
    ? selectedElements[0]
    : null;
  const renderGradientControls = (pattern, onCommit, commitRef) =>
    updateSelectedElements &&
    shouldEditSelectedElements &&
    canvasElement &&
    !canvasElement.isLocked &&
    !canvasElement.isHidden ? (
      <GradientCanvasControls
        pattern={pattern}
        element={canvasElement}
        bounds={geometryBounds}
        changedStyle={changedStyle || 'background'}
        editorState={hasCurrentEditor ? editorState : null}
        pickerId={pickerId}
        onCommit={onCommit}
        commitRef={commitRef}
        onPaintMeasure={setPaintMeasurement}
      />
    ) : null;

  // Handle cases where a synced saved colour is updated by history (redo or undo).
  // Non-syncing consumers use their color prop as the authoritative pattern.
  useEffect(() => {
    if (!savedColorWithId) {
      return;
    }

    setEditingColor((prev) => {
      if (prev?.color.id === savedColorWithId?.color.id) {
        return { ...prev, color: savedColorWithId.color };
      }

      return prev;
    });
  }, [savedColorWithId]);

  useEffect(() => {
    return () => {
      setEditingColor(null);
    };
  }, []);

  return (
    <CSSTransition in appear classNames="picker" timeout={300}>
      <Container
        role="dialog"
        aria-label={__('Color and gradient picker', 'web-stories')}
        ref={containerRef}
        onMouseDownCapture={handlePointerInteractionStart}
        onPointerDownCapture={handlePointerInteractionStart}
      >
        {editingColor ? (
          <SavedColorEditor
            key={editingColor.id}
            geometryBounds={paintMeasurement?.bounds || geometryBounds}
            legacyPaint={paintMeasurement?.legacyPaint || legacyPaint}
            renderGradientControls={renderGradientControls}
            allowsGradient={allowsGradient}
            allowsOpacity={allowsOpacity}
            color={editingColor}
            handleGoBack={hideCustomPicker}
            handleClose={handleCloseAndRefocus}
            handleColorChange={handleColorChange}
            handleDelete={(deletedColor) => {
              onDebounceUpdateSavedColors.cancel();
              updateInlineGradientStyles({
                action: 'delete',
                color: deletedColor.color,
              });
              if (syncSavedColors) {
                updateSavedColors({
                  action: 'delete',
                  changedStyle,
                  editedColor: deletedColor,
                  selectedElementIds,
                  editingElementId: hasCurrentEditor ? editingElementId : null,
                });
              }
            }}
            handleSavedColorChange={(newSavedColor) => {
              onDebounceUpdateSavedColors.cancel();
              if (syncSavedColors) {
                updateSavedColors({
                  action: 'addOrEdit',
                  changedStyle,
                  editedColor: newSavedColor,
                  selectedElementIds,
                  editingElementId: hasCurrentEditor ? editingElementId : null,
                });
              }
            }}
            setOnPickerClose={setOnPickerClose}
          >
            {children}
          </SavedColorEditor>
        ) : (
          <>
            <SavedColorPicker
              allowsGradient={allowsGradient}
              allowsOpacity={allowsOpacity}
              color={color}
              handleColorChange={handleColorChange}
              handleClose={handleCloseAndRefocus}
              handleDelete={(deletedColor) => {
                updateInlineGradientStyles({
                  action: 'delete',
                  color: deletedColor.color,
                });
                if (syncSavedColors) {
                  updateSavedColors({
                    action: 'delete',
                    changedStyle,
                    editedColor: deletedColor,
                    selectedElementIds,
                    editingElementId: hasCurrentEditor
                      ? editingElementId
                      : null,
                  });
                }
              }}
              savedColors={savedColors}
              showCustomPicker={showCustomPicker}
              handleApplyColor={handleApplyColor}
            />
          </>
        )}
      </Container>
    </CSSTransition>
  );
}

ColorPicker.propTypes = {
  legacyPaint: PropTypes.oneOf(['css', 'svg', 'filter']),
  isPageBackground: PropTypes.bool,
  geometryBounds: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  allowsGradient: PropTypes.bool,
  allowsOpacity: PropTypes.bool,
  editColorOnOpen: PropTypes.bool,
  onEditingColorChange: PropTypes.func,
  isEyedropperActive: PropTypes.bool,
  children: PropTypes.node,
  color: PatternPropType,
  changedStyle: PropTypes.string,
  syncSavedColors: PropTypes.bool,
  updateSelectedElements: PropTypes.bool,
  debounceLeading: PropTypes.bool,
  deferCommitUntilPointerUp: PropTypes.bool,
  onPreviewChange: PropTypes.func,
  onDimensionChange: PropTypes.func,
  setOnPickerClose: PropTypes.func.isRequired,
};

export default ColorPicker;
